import * as THREE from 'three'
import { v4 as uuid } from 'uuid'
import { ThreeItemKeys, ThreeItemTypes } from '../constants/three'
import { materialColors } from '../constants/theme'
import { storeScene } from '../stores/scene'
import { get } from 'svelte/store'
import { camera } from '../stores/camera'
import {
  MaxiPrinterDriver,
  MiniPrinterLDriver,
  MiniPrinterXLDriver,
} from './printer-drivers'
import { makeBoxGeometryFromCorner } from '../helpers/three'

class PrinterService {
  static async buildGroup(model: PrinterModel, scene?: THREE.Scene | null) {
    if (!scene) {
      throw new Error('Scene is required to build printer group')
    }

    const printerDriver = this.getPrinterDriver(model)

    const storedNextIndex = scene.userData[ThreeItemKeys.PRINTER_NEXT_INDEX]
    const nextIndex = typeof storedNextIndex === 'number'
      ? storedNextIndex
      : this.getPrintersCountFromScene(scene)
    scene.userData[ThreeItemKeys.PRINTER_NEXT_INDEX] = nextIndex + 1

    const printerGroup = new THREE.Group()
    printerGroup.name = `${printerDriver.name} #${nextIndex + 1}`
    printerGroup.userData = {
      ...printerDriver.getDefaultUserData(),
      [ThreeItemKeys.GRID_TYPE]: printerDriver.defaultGrid,
      [ThreeItemKeys.ITEM_TYPE]: ThreeItemTypes.PRINTER,
      [ThreeItemKeys.PRINTER_COLOR]: materialColors[nextIndex % materialColors.length],
      [ThreeItemKeys.PRINTER_ID]: uuid(),
      [ThreeItemKeys.PRINTER_MODEL]: model,
      [ThreeItemKeys.PRINTER_OPTIONS]: printerDriver.getDefaultOptions(),
      [ThreeItemKeys.ROTATION_LOCKED_X]: true,
      [ThreeItemKeys.ROTATION_LOCKED_Y]: true,
      [ThreeItemKeys.ROTATION_LOCKED_Z]: false,
      [ThreeItemKeys.TRANSLATION_LOCKED_X]: false,
      [ThreeItemKeys.TRANSLATION_LOCKED_Y]: false,
      [ThreeItemKeys.TRANSLATION_LOCKED_Z]: true,
    }

    const printerMesh = await printerDriver.getMesh(nextIndex)

    if (printerMesh) {
      printerGroup.add(printerMesh)
    }

    return printerGroup
  }

  static async showFloorZone(scene: THREE.Scene | null, printerUuid: string, zoneType: FloorGridType) {
    const printerGroup = await this.getPrinterFromScene(scene, printerUuid)

    await this.changePolarZoneVisible(printerGroup, zoneType === 'polar' || zoneType === 'both')
    await this.changeCartesianZoneVisible(printerGroup, zoneType === 'cartesian' || zoneType === 'both')
  }

  static async showPrintableZone(scene: THREE.Scene | null, printerUuid: string, zoneType: PrintableZoneType) {
    const printerGroup = await this.getPrinterFromScene(scene, printerUuid)
    const printerDriver = this.getPrinterDriver(printerGroup.userData[ThreeItemKeys.PRINTER_MODEL])

    let foundZone: THREE.Object3D | undefined

    printerGroup.traverse((child) => {
      if (child.userData[ThreeItemKeys.ITEM_TYPE] === ThreeItemTypes.PRINTABLE_ZONE) {
        foundZone = child
      }
    })

    foundZone?.removeFromParent()
    if (zoneType !== 'none') {
      const printerZone = await printerDriver.getPrintableZone(zoneType, printerGroup.userData[ThreeItemKeys.PRINTER_COLOR])
      printerZone.userData[ThreeItemKeys.ASSIGNED_PRINTER_ID] = printerUuid
      printerZone.userData[ThreeItemKeys.ITEM_TYPE] = ThreeItemTypes.PRINTABLE_ZONE

      printerGroup.add(printerZone)

      if (zoneType === 'half') {
        this.addCameraTrackingToHalfPrintableZone(printerZone)
      }
    }
  }

  static async showOptionConstraintZones(scene: THREE.Scene | null, printerUuid: string) {
    const printerGroup = await this.getPrinterFromScene(scene, printerUuid)
    const printerDriver = this.getPrinterDriver(printerGroup.userData[ThreeItemKeys.PRINTER_MODEL])
    const printerOptions: Record<string, string> = printerGroup.userData[ThreeItemKeys.PRINTER_OPTIONS] ?? {}

    // Remove existing option constraint zones
    const toRemove: THREE.Object3D[] = []
    printerGroup.traverse((child) => {
      if (child.userData[ThreeItemKeys.ITEM_TYPE] === ThreeItemTypes.OPTION_CONSTRAINT_ZONE) {
        toRemove.push(child)
      }
    })
    toRemove.forEach(child => child.removeFromParent())

    // Add new constraint zones based on current options
    const constraintZones = printerDriver.getOptionConstraintZones(printerOptions)

    for (const zone of constraintZones) {
      const widthM = zone.width_MM / 1000
      const depthM = zone.depth_MM / 1000
      const heightM = zone.height_MM / 1000
      const offsetXM = zone.offsetX_MM / 1000
      const offsetYM = zone.offsetY_MM / 1000
      const offsetZM = zone.offsetZ_MM / 1000

      const geometry = makeBoxGeometryFromCorner(widthM, depthM, heightM)
      const material = new THREE.MeshStandardMaterial({
        color: 'red',
        roughness: 0.3,
        metalness: 0.3,
        transparent: true,
        opacity: 0.3,
        side: THREE.DoubleSide,
      })

      const mesh = new THREE.Mesh(geometry, material)
      mesh.position.set(offsetXM, offsetYM, offsetZM)
      mesh.userData[ThreeItemKeys.ITEM_TYPE] = ThreeItemTypes.OPTION_CONSTRAINT_ZONE
      printerGroup.add(mesh)
    }
  }

  static async changeVisibility(scene: THREE.Scene | null, printerUuid: string, visibility: 'visible' | 'hidden') {
    const printer = await this.getPrinterFromScene(scene, printerUuid)
    printer.visible = visibility === 'visible'
    return printer
  }

  static async removeFromScene(scene: THREE.Scene | null, printerUuid: string) {
    const printer = await this.getPrinterFromScene(scene, printerUuid)
    printer.removeFromParent()
  }

  private static async getPrinterFromScene(scene: THREE.Scene | null, printerUuid: string) {
    const foundPrinter = scene?.getObjectByProperty('uuid', printerUuid)

    if (foundPrinter?.userData[ThreeItemKeys.ITEM_TYPE] !== ThreeItemTypes.PRINTER) {
      throw new Error('Printer not found')
    }

    return foundPrinter
  }

  static async changePolarZoneVisible(printerGroup: THREE.Object3D, visible: boolean) {
    const printerDriver = this.getPrinterDriver(printerGroup.userData[ThreeItemKeys.PRINTER_MODEL])
    let existingPolarZone: THREE.Object3D | undefined

    printerGroup.traverse((child) => {
      if (child.userData[ThreeItemKeys.ITEM_TYPE] === ThreeItemTypes.POLAR_ZONE) {
        existingPolarZone = child
      }
    })

    if (existingPolarZone && !visible) {
      printerGroup.remove(existingPolarZone)
    }
    else if (visible && printerDriver.hasPolarGrid && !existingPolarZone) {
      const polarZone = await printerDriver.getPolarZone(printerGroup.userData[ThreeItemKeys.PRINTER_COLOR])
      printerGroup.add(polarZone)
    }
  }

  static async changeCartesianZoneVisible(printerGroup: THREE.Object3D, visible: boolean) {
    const printerDriver = this.getPrinterDriver(printerGroup.userData[ThreeItemKeys.PRINTER_MODEL])
    let existingCartesianZone: THREE.Object3D | undefined

    printerGroup.traverse((child) => {
      if (child.userData[ThreeItemKeys.ITEM_TYPE] === ThreeItemTypes.CARTESIAN_ZONE) {
        existingCartesianZone = child
      }
    })

    if (existingCartesianZone && !visible) {
      printerGroup.remove(existingCartesianZone)
    }
    else if (visible && printerDriver.hasCartesianGrid && !existingCartesianZone) {
      printerGroup.add(await printerDriver.getCartesianZone(printerGroup.userData[ThreeItemKeys.PRINTER_COLOR]))
    }
  }

  private static addCameraTrackingToHalfPrintableZone(item: THREE.Object3D) {
    function updateMeshRotationToFaceCameraZ(mesh: THREE.Object3D, camera: THREE.Camera, parent: THREE.Object3D) {
      // Get position of mesh and camera in world space
      const meshPosition = new THREE.Vector3()
      const cameraPosition = new THREE.Vector3()
      mesh.getWorldPosition(meshPosition)
      camera.getWorldPosition(cameraPosition)

      // Compute direction from mesh to camera in X-Y plane
      const dx = cameraPosition.x - meshPosition.x
      const dy = cameraPosition.y - meshPosition.y

      // Compute the angle in the X-Y plane (Z is up)
      const angle = Math.atan2(dy, dx)

      // Apply rotation around Z-axis only
      mesh.rotation.y = (angle + Math.PI / 2) - parent.rotation.z
    }

    function animate() {
      const scene = get(storeScene)
      const existingZone = scene?.getObjectByProperty('uuid', item.uuid)

      if (existingZone?.visible && existingZone?.parent) {
        requestAnimationFrame(animate)

        updateMeshRotationToFaceCameraZ(item, get(camera), existingZone.parent)
      }
    }

    animate()
  }

  static getPrinterDriver(model: PrinterModel) {
    switch (model) {
      case 'maxiprinter':
        return MaxiPrinterDriver
      case 'miniprinter-l':
        return MiniPrinterLDriver
      case 'miniprinter-xl':
        return MiniPrinterXLDriver
      default:
        console.warn(`Unknown printer model: ${model}, defaulting to MaxiPrinter`)
        return MaxiPrinterDriver
    }
  }

  private static getPrintersCountFromScene(scene: THREE.Scene) {
    let count = 0

    scene.traverse((object) => {
      if (object.userData[ThreeItemKeys.ITEM_TYPE] === ThreeItemTypes.PRINTER) {
        count += 1
      }
    })

    return count
  }
}

export default PrinterService
